import React, { useState, useEffect, useCallback, useContext } from "react";
import { View, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PropTypes from 'prop-types';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip,
  Legend as RechartsLegend, ResponsiveContainer,
  LabelList,
  Cell
} from 'recharts';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import Permiso from "../../router/Permiso";
import Input from "../../components/Input";
import 'react-circular-progressbar/dist/styles.css';
import RadialSeparators from "../../components/RadialSeparators";
import { ReciboService } from "../../services/ReciboService";
import { AuthContext } from "../../context/AuthContext";

const ReceiptsAdmin = ({ refreshTrigger = 0 }) => {
  const { token } = useContext(AuthContext);
  const [codeSale, setCodeSale] = useState("");
  const [clientName, setClientName] = useState("");
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [loading, setLoading] = useState(false);
  const [ventas, setVentas] = useState([]);
  const [productivityList, setProductivityList] = useState([]);
  const [percentStatistic, setPercentStatistic] = useState(0);
  const COLORS = ['#22b04c', '#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f', '#edc948', '#b07aa1'];

  const navigate = useNavigate();

  const getStatistics = useCallback(async () => {
    try {
      const params = {
        token,
        fechaInicio: fechaInicio && fechaInicio.trim() !== "" ? fechaInicio : null,
        fechaFin: fechaFin && fechaFin.trim() !== "" ? fechaFin : null
      };
      const result = await ReciboService.getStatisticsInstaladas(params);
      const percent = result.pagados > 0 ? Math.round((result.pagados / result.total_recibos) * 100) : 0;
      setPercentStatistic(percent);
      const productivityList = result.detallePorUsuario.map(u =>
        ({ name: u.nombre, cantidad: u.total_contactos }));
      setProductivityList(productivityList);
    } catch (err) {
      console.error("Error en estadísticas:", err);
    }
  }, [token, fechaInicio, fechaFin]);

  const exportarExcel = async () => {
    try {
      const filtros = {
        desde: fechaInicio,
        hasta: fechaFin,
        dni: codeSale,
      };

      const blob = await ReciboService.exportarVentasInstaladasSeguimientoExcel(token, filtros);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "listado_cobranzas.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error al exportar Excel:", error);
    }
  };

  const fetchVentas = useCallback(async () => { 
    setLoading(true);
    setError("");
    setClientName("");
    try {
      const { ventas, totalPaginas, totalRegistros } = await ReciboService.fetchVentasInstaladaSeguimiento({
        token,
        pagina: currentPage,
        pageSize: 20,
        desde: fechaInicio,
        hasta: fechaFin,
        dni: codeSale,
        nombre: clientName,
      });
      setVentas(ventas);
      setTotalPages(totalPaginas);
      setTotalRegistros(totalRegistros);
    } catch (err) {
      setError("Error al cargar las ventas");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, fechaInicio, fechaFin, codeSale, clientName]);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchVentas(), getStatistics()]);
    };
    loadData();
  }, [fetchVentas, getStatistics, refreshTrigger]);

  return (
    <div className="ventas-admin-content">
      <div className="ventas-admin-tab-content">

        <div className="flex w-full">
          <div className="w-20">
            <div className="ventas-admin-subtitle">
              Cobranzas
            </div>

          </div>
          <div className="w-20">
            <div className="ventas-admin-subtitle pb-10">
              Avance de Gestión
            </div>
            <CircularProgressbarWithChildren
              value={percentStatistic}
              text={`${percentStatistic}%`}
              strokeWidth={10}
              styles={buildStyles({
                strokeLinecap: "butt",
                pathColor: "#8a6fcb",
                textColor: "black"
              })}
            >
              <RadialSeparators
                count={12}
                style={{
                  background: "#fff",
                  width: "2px",
                  height: `${10}%`
                }}
              />
            </CircularProgressbarWithChildren>
          </div>
          <div className="w-40">
            <div className="ventas-admin-subtitle pb-10">
              Productividad
            </div>
            {productivityList.length > 0 && (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={productivityList}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip />
                  <RechartsLegend />
                  <Bar dataKey="cantidad" name="Contacto diario por usuario">
                    {productivityList.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                    <LabelList dataKey="cantidad" position="top" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="ventas-table-header">
          <h2 className="ventas-table-title">Lista de clientes </h2>
          <div className="productos-actions">
            <Permiso permiso="export_cobranza">
              <button onClick={exportarExcel} className="btn-exportar">
                Exportar a Excel
              </button>
            </Permiso>
          </div>
        </div>

        <div className="usuarios-filtros ">
          <Input title="Código de venta:" value={codeSale} setValue={setCodeSale} placeholder="Ingrese número" className="filtro-item" />
          <div className="filtro-item">
            <label htmlFor="fecha-inicio">Fecha Inicio:</label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
            />
          </div>
          <div className="filtro-item">
            <label htmlFor="fecha-fin">Fecha Fin:</label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
            />
          </div>
          <div className="filtro-item">
            <button onClick={fetchVentas} className="btn-filtrar">
              Buscar
            </button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <div className="loading">Cargando ventas...</div>
        ) : (
          <>
            <div className="ventas-table-wrapper">
              <table className="ventas-table">
                <thead>
                  <tr>
                    <th>Codigo</th>
                    <th>Cliente</th>
                    <th>F. Venta</th>
                    <th>Vendedor</th>
                    <th>Supervisor</th>
                    <th>Vencidos</th>
                    <th>Pagados</th>
                    <th>#Contact</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {ventas.map((venta) => {

                    return (
                      <tr key={venta.id}>
                        <td>{venta.codigo_venta_cliente}</td>
                        <td>
                          {venta.cliente_nombre}  {venta.cliente_apellido_paterno}  {venta.cliente_apellido_materno}
                        </td>
                        <td>{new Date(venta.fecha_venta).toLocaleDateString()}</td>
                        <td>
                          {venta.nombre_vendedor} {venta.apellido_vendedor}
                        </td>
                        <td>
                          {venta.supervisor}
                        </td>
                        <td>{venta.recibos_vencidos}</td>
                        <td>{venta.recibos_pagados}</td>
                        <td>{venta.item_correlativo}</td>
                        <td className="ventas-acciones">
                          <Permiso permiso="view_cobranza">
                            <span
                              style={{ cursor: "pointer" }}
                              onClick={() => navigate(`/admin/recibos/registrar/${venta.id}/only`)}
                            >
                              <View size={20} />
                            </span>
                          </Permiso>
                          <Permiso permiso="add_seguimiento_cobranza">
                            <span
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                navigate(`/admin/recibos/registrar/${venta.id}`);
                              }}
                            >
                              <Phone size={20} />
                            </span>
                          </Permiso>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <span style={{ marginLeft: "12px", fontSize: "14px", color: "#555" }}>
              {" "}
              Total de registros: {totalRegistros}{" "}
            </span>
            {/* Paginación */}
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Anterior
              </button>
              <span>
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Siguiente
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

ReceiptsAdmin.propTypes = {
  refreshTrigger: PropTypes.number
};

export default ReceiptsAdmin;
